// positionStore.js - the ONE source of truth for Solana positions.
//
// Positions are keyed by mint in localStorage. React state (useTrading's
// `positions`, a chart's own-token view) is only a READ of this store -
// never a second copy that has to be kept in sync. Every write goes through
// set(), which notifies subscribers with the mint that changed, so any view
// of that mint re-reads. A bot order, chart 2, or a TOKEN_SWITCH exit can
// therefore only ever touch the list for its own token.
//
// Futures positions are NOT here - they come from the HL poll.
//
// positions-20261017: keyed by WALLET:MINT. The per-wallet master lives at
// solcheddar_pos_<wallet>_<mint>; the old key solcheddar_positions_<mint> is
// kept as the ACTIVE wallet's mirror, so every existing reader (this get(),
// WalletHoldings, BotSettingsMenu) keeps working unchanged. setWallet()
// swaps the mirrors when the wallet changes. Pre-20261017 entries (no
// `wallet` field) are adopted by the first wallet that connects and then
// verified against the chain by positionSync (closed ones drop after two
// zero-balance reads).
const KEY = (t) => `solcheddar_positions_${t}`
const PREFIX = 'solcheddar_positions_'
const MKEY = (w, t) => `solcheddar_pos_${w}_${t}`
const WALLET_KEY = 'solcheddar_pos_wallet'
const subs = new Set()

let wallet = ''
try { wallet = localStorage.getItem(WALLET_KEY) || '' } catch {}

const readList = (k) => {
  try {
    const v = JSON.parse(localStorage.getItem(k) || '[]')
    return Array.isArray(v) ? v.filter((p) => p && typeof p === 'object') : []
  } catch { return [] }
}
const notify = (t) => subs.forEach((fn) => { try { fn(t) } catch {} })

export const positionStore = {
  get(t) {
    if (!t) return []
    return readList(KEY(t))
  },
  set(t, list) {
    if (!t) return
    const out = (Array.isArray(list) ? list : []).map((p) => (wallet && p && !p.wallet ? { ...p, wallet } : p))
    try {
      if (out.length) localStorage.setItem(KEY(t), JSON.stringify(out))
      else localStorage.removeItem(KEY(t))
      if (wallet) {
        if (out.length) localStorage.setItem(MKEY(wallet, t), JSON.stringify(out))
        else localStorage.removeItem(MKEY(wallet, t))
      }
    } catch {}
    notify(t)
  },
  // Mints that have a record for the active wallet.
  mints() {
    const out = []
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k && k.startsWith(PREFIX)) out.push(k.slice(PREFIX.length))
      }
    } catch {}
    return out
  },
  getWallet() { return wallet },
  // Switch the active wallet: rebuild the mirrors from that wallet's masters.
  setWallet(w) {
    w = w || ''
    if (!w || w === wallet) { if (w) wallet = w; return }
    try {
      const keys = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        if (k) keys.push(k)
      }
      const mprefix = `solcheddar_pos_${w}_`
      const seen = new Set()
      for (const k of keys) {
        if (!k.startsWith(PREFIX)) continue
        const mint = k.slice(PREFIX.length)
        seen.add(mint)
        const master = localStorage.getItem(MKEY(w, mint))
        if (master) { localStorage.setItem(k, master); continue }
        const list = readList(k)
        const mine = list.length > 0 && list.every((p) => !p.wallet || p.wallet === w)
        if (mine) {
          const adopted = list.map((p) => ({ ...p, wallet: w }))
          const s = JSON.stringify(adopted)
          localStorage.setItem(k, s)
          localStorage.setItem(MKEY(w, mint), s)
        } else {
          localStorage.removeItem(k)
        }
      }
      for (const k of keys) {
        if (!k.startsWith(mprefix)) continue
        const mint = k.slice(mprefix.length)
        if (seen.has(mint)) continue
        const v = localStorage.getItem(k)
        if (v) localStorage.setItem(KEY(mint), v)
      }
      localStorage.setItem(WALLET_KEY, w)
    } catch {}
    wallet = w
    notify('*')
  },
  subscribe(fn) {
    subs.add(fn)
    return () => subs.delete(fn)
  },
}